import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { api, fileUrl } from "../api/client";
import type { Box, Inspection } from "../api/types";
import { useSession } from "../store/session";
import { Button, Card, cx, Skeleton } from "../components/Controls";
import { AiBadge, DemoTag, VerdictBadge } from "../components/Badge";
import { VerdictStamp } from "../components/VerdictStamp";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { fmtDateTime, fmtNum } from "../lib/format";
import { NAV_KEY } from "./InspectionsView";

function RetryImage({ src, alt, box }: { src: string; alt: string; box?: Box | null }) {
  const [attempt, setAttempt] = useState(0);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setAttempt(0);
    setFailed(false);
  }, [src]);
  const url = attempt ? `${src}${src.includes("?") ? "&" : "?"}r=${attempt}` : src;
  if (failed) return <div className="flex aspect-[4/3] items-center justify-center rounded-md bg-slate-100 text-sm text-slate-400" data-testid="inspection-image-missing">Imagen no disponible</div>;
  return (
    <div className="reticle overflow-hidden rounded-lg">
      <img
        src={url}
        alt={alt}
        className="w-full bg-slate-100"
        onLoad={(e) => setSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        onError={() => (attempt === 0 ? setTimeout(() => setAttempt(1), 2000) : setFailed(true))}
        data-testid="inspection-main-image"
      />
      {box && size && (
        <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox={`0 0 ${size.w} ${size.h}`} preserveAspectRatio="none" data-testid="anomaly-box-overlay">
          <rect x={box.x} y={box.y} width={box.w} height={box.h} fill="none" stroke="#C42A1D" strokeWidth={Math.max(3, size.w / 250)} />
        </svg>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 text-sm">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  );
}

export default function InspectionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { session } = useSession();
  const [insp, setInsp] = useState<Inspection | null>(null);
  const [missing, setMissing] = useState(false);
  const [view, setView] = useState<"original" | "heatmap">("original");
  const [confirm, setConfirm] = useState(false);
  const nav = JSON.parse(sessionStorage.getItem(NAV_KEY) || '{"ids":[],"back":"/inspecciones"}');
  const idx = nav.ids.indexOf(id);

  useEffect(() => {
    setInsp(null);
    setMissing(false);
    setView("original");
    api.get<Inspection>(`/inspections/${id}`).then(setInsp).catch(() => setMissing(true));
  }, [id]);

  if (missing) return <p className="text-slate-500">Inspección no encontrada.</p>;
  if (!insp) return <Skeleton className="h-96 w-full" />;
  const src = view === "heatmap" ? fileUrl(insp.heatmap_url) : fileUrl(insp.image_url);
  return (
    <div className="animate-rise space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to={nav.back || "/inspecciones"} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-ink" data-testid="back-to-inspections-link"><ArrowLeft className="h-4 w-4" /> Inspecciones</Link>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" disabled={idx <= 0} onClick={() => navigate(`/inspecciones/${nav.ids[idx - 1]}`)} data-testid="inspection-prev-button"><ChevronLeft className="h-4 w-4" /> Anterior</Button>
          <Button variant="secondary" size="sm" disabled={idx < 0 || idx >= nav.ids.length - 1} onClick={() => navigate(`/inspecciones/${nav.ids[idx + 1]}`)} data-testid="inspection-next-button">Siguiente <ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-[1.6fr,1fr]">
        <Card className="p-3">
          {insp.heatmap_url && (
            <div className="mb-3 inline-flex rounded-md bg-slate-100 p-1">
              {(["original", "heatmap"] as const).map((v) => (
                <button key={v} onClick={() => setView(v)} className={cx("rounded px-3 py-1 text-sm", view === v ? "bg-white font-semibold shadow-sm" : "text-slate-500")} data-testid={`inspection-view-${v}`}>{v === "original" ? "Original" : "Heatmap"}</button>
              ))}
            </div>
          )}
          {src ? <RetryImage key={src} src={src} alt="Inspección" box={view === "original" ? insp.anomaly_box : null} /> : <div className="flex aspect-[4/3] items-center justify-center rounded-md bg-slate-100 text-sm text-slate-400">Sin foto</div>}
        </Card>
        <Card className="p-5" data-testid="inspection-detail-panel">
          {(insp.operator_verdict || insp.ai_result) && (
            <div className="mb-4 flex items-center gap-4 border-b border-line pb-4">
              <VerdictStamp result={(insp.operator_verdict || insp.ai_result) as "OK" | "NG"} size="lg" testId="inspection-verdict-stamp" />
              <div className="text-sm text-slate-500">{insp.operator_verdict ? "Veredicto del operario" : "Resultado de la IA, sin veredicto del operario"}</div>
            </div>
          )}
          <div className="mb-2 flex items-center gap-2">
            <span className="font-mono text-xs text-slate-400">{insp.id.slice(0, 8)}</span>
            {insp.is_demo && <DemoTag />}
          </div>
          <dl className="divide-y divide-slate-100">
            <Row label="Resultado de IA">
              {insp.ai_result ? <span className="inline-flex items-center gap-2"><AiBadge result={insp.ai_result} score={insp.ai_score} /> umbral {fmtNum(insp.ai_threshold)}</span> : <span className="text-slate-500" data-testid="inspection-no-ai">sin IA (registro manual)</span>}
            </Row>
            {insp.ai_result && <Row label="Score">{fmtNum(insp.ai_score, 4)}</Row>}
            <Row label="Veredicto"><span className="inline-flex items-center gap-2"><VerdictBadge value={insp.operator_verdict} />{insp.verdict_seconds !== null && `${fmtNum(insp.verdict_seconds, 1)} s`}</span></Row>
            <Row label="Fecha">{fmtDateTime(insp.timestamp)}</Row>
            <Row label="Operario">{insp.operator?.name || "—"}</Row>
            <Row label="Pieza">{insp.part ? `${insp.part.part_number} · ${insp.part.name}` : "—"}</Row>
            <Row label="Punto">{insp.point_index + 1} · {insp.point_name || "—"}</Row>
            <Row label="Dispositivo"><span className="font-mono text-xs">{insp.device_id}</span></Row>
            <Row label="Versión de app"><span className="font-mono text-xs">{insp.app_version || "—"}</span></Row>
            <Row label="Latencia">{insp.latency_ms === null ? "—" : `${insp.latency_ms} ms`}</Row>
            <Row label="Lote">{insp.batch_code || "—"}</Row>
            <Row label="Muestra / repetición">{insp.sample_id || "—"} / {insp.repetition ?? "—"}</Row>
            <Row label="Modo estudio">{insp.study_mode ? "Sí" : "No"}</Row>
            <Row label="Origen">{insp.source === "manual" ? "Registro manual" : "Lente"}</Row>
          </dl>
          {session.operator.role === "admin" && (
            <Button variant="danger" size="sm" className="mt-4" onClick={() => setConfirm(true)} data-testid="delete-inspection-button"><Trash2 className="h-3.5 w-3.5" /> Borrar inspección</Button>
          )}
        </Card>
      </div>
      <ConfirmDialog open={confirm} onClose={() => setConfirm(false)} danger title="Borrar inspección" message="Se borra la inspección y sus fotos." confirmLabel="Borrar" onConfirm={async () => { await api.del(`/inspections/${insp.id}`); navigate(nav.back || "/inspecciones"); }} />
    </div>
  );
}
